<template>
  <TwoColumnLayout :extra-height-offset="extraHeightOffset">
    <template #left>
      <div class="tw-flex tw-align-top tw-justify-between tw-mb-4">
        <div class="tw-text-2xl tw-font-medium">
          {{ isEditing[meeting.id] ? copy.editTitle : copy.meetingSummary }}
        </div>

        <SfButtonDs
          v-if="!isEditing[meeting.id]"
          variant="tertiary"
          icon-only
          icon-before="pencil"
          :aria-label="copy.edit"
          class="tw-ml-4 tw-flex-none"
          data-testid="past-meeting-edit-button"
          data-heap="talking-points-edit-open"
          @click="meetingDetails.edit(meeting)"
        />
        <div v-else class="tw-flex tw-items-center tw-gap-4">
          <SfButtonDs
            :class="meetingSummaryButtonClasses"
            variant="secondary"
            :disabled="isSaving[meeting.id]"
            data-heap="talking-points-edit-cancel"
            @click="meetingDetails.reset(meeting)"
          >
            <i class="fa fa-close" />
          </SfButtonDs>
          <SfButtonDs
            :class="meetingSummaryButtonClasses"
            variant="primary"
            :disabled="isSaving[meeting.id]"
            data-testid="save-summary-button"
            data-heap="talking-points-edit-save"
            @click="handleSaveMeetingDetails(meeting)"
          >
            <div v-if="isSaving[meeting.id]" class="tw-mr-1 sf-spinner" />
            <i v-else class="fa fa-check" />
          </SfButtonDs>
        </div>
      </div>
      <div
        class="
          tw-bg-slate-50
          tw-rounded-lg
          tw-p-6
          tw-pt-4
          tw-text-sf-neutral-darker-1
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <div
          v-if="!isEditing[meeting.id]"
          v-dompurify-html="htmlSummary[meeting.id]"
        ></div>
        <MeetingForm v-else :meeting-id="meeting.id" />
      </div>
      <div
        v-if="meeting.attachments.length"
        class="tw-text-2xl tw-font-medium tw-my-4"
      >
        {{ copy.attachments }}
      </div>
      <div
        v-if="meeting.attachments.length"
        class="tw-flex tw-flex-wrap tw-gap-1"
      >
        <SfDocumentPill
          v-for="attachment in meeting.attachments"
          :id="attachment.id"
          :key="attachment.id"
          :viewing-url="attachment.url"
          :allow-delete="false"
        >
          <template #left-icon>
            <i
              class="
                fa fa-file
                tw-text-xs tw-text-sf-neutral-light-2
                hover:tw-text-sf-gray-light
              "
            ></i>
          </template>
          <template #default>
            {{ attachment.filename }}
          </template>
        </SfDocumentPill>
      </div>
      <div
        v-if="meeting.links.length"
        class="tw-text-2xl tw-font-medium tw-my-4"
      >
        {{ copy.quickLinks }}
      </div>
      <LinksSection :links="meeting.links" />
    </template>
    <template #right>
      <MeetingRemarks :meeting="meeting" />
    </template>
  </TwoColumnLayout>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfDocumentPill from '@app/shared/vue/components/sf-document-pill.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

import type { Meeting } from '@app/application/client_meetings/types'
import LinksSection from '@app/application/client_meetings/screens/prepare/components/links-section.vue'
import MeetingForm from '@app/application/client_meetings/screens/past/components/meeting-form.vue'
import MeetingRemarks from '@app/application/client_meetings/screens/past/components/meeting-remarks.vue'
import TwoColumnLayout from '@app/application/client_meetings/screens/past/components/two-column-layout.vue'
import { useMeetingDetailsStore } from '@app/application/client_meetings/screens/past/stores/meeting-details'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'

const I18n = useI18n()
const copy = {
  editTitle: I18n.t('client_meetings.past.meeting_form.edit_title'),
  edit: I18n.t('common.edit'),
  meetingSummary: I18n.t('client_meetings.past.meeting_summary'),
  attachments: I18n.t('client_meetings.past.attachments'),
  quickLinks: I18n.t('client_meetings.prepare.links.quick_links'),
}

interface Props {
  meeting: Meeting
}

defineProps<Props>()

const meetingDetails = useMeetingDetailsStore()
const { isEditing, isSaving, title, completedDate, summary } =
  storeToRefs(meetingDetails)

const pastMeetingsStore = usePastMeetingsStore()
const { htmlSummary } = storeToRefs(pastMeetingsStore)

// This height offset represents the distance from the bottom line of
// the navbar-tabs until the top line of the two-column layout container when the
// meeting card is expanded and scrolled into view (at the top of the viewport)
const extraHeightOffset = 110

const meetingSummaryButtonClasses = 'tw-w-9 tw-h-9 tw-flex-none'

async function handleSaveMeetingDetails(meeting: Meeting): Promise<void> {
  const updatedMeeting = {
    ...meeting,
    ...{
      title: title.value[meeting.id],
      completedAt: completedDate.value[meeting.id] || '',
      summary: summary.value[meeting.id],
    },
  }

  meetingDetails.setIsSaving(updatedMeeting)
  await pastMeetingsStore.save(updatedMeeting)
  meetingDetails.setIsSaved(updatedMeeting)
}
</script>
